import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import {
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaFilter,
  FaUserCheck,
  FaTimes,
  FaBan,
  FaBell,
  FaCommentDots,
  FaHeadphones,
  FaHeadset,
  FaQuestionCircle,
  FaThumbsDown,
  FaMicrophoneSlash,
  FaClone,
  FaPhoneSlash,
  FaTrash,
  FaHourglassHalf,
  FaFlag,
  FaMinusCircle,
  FaSearch,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { useFicheDetailModal } from '../contexts/FicheDetailModalContext';
import { useSidebar } from '../contexts/SidebarContext';
import { toast } from 'react-toastify';
import FicheDetailLink from '../components/FicheDetailLink';
import RemarquesContent from '../components/RemarquesContent';
import KoMotifModal from '../components/KoMotifModal';
import { getEtatsGroupedByPhase } from '../utils/etatsByPhase';
import SystemMessageBanner from '../components/SystemMessageBanner';
import { getFicheRowByEtatClassName } from '../utils/etatColorContrast';
import {
  FicheRowIndexHeader,
  FicheRowIndexCell,
  getFicheRowNumber,
} from '../components/fiches/FicheRowIndexColumn';
import './ControleQualite.css';
import useForceDesktopViewport from '../hooks/useForceDesktopViewport';

// ID de l'état KO et HC
const ETAT_KO_ID = 54;
const ETAT_HC_ID = 55;
const PAGE_SIZE_MULTI_DAY = 50;

const normalizeEtatTitre = (titre) =>
  String(titre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

/** Trouve l'état « BRUT » (état initial qualification) dans la liste des états. */
const findEtatBrut = (etats) => {
  if (!Array.isArray(etats)) return null;
  return (
    etats.find((e) => normalizeEtatTitre(e.titre) === 'brut') ||
    etats.find((e) => normalizeEtatTitre(e.abbreviation) === 'brut') ||
    etats.find((e) => normalizeEtatTitre(e.titre).includes('brut')) ||
    null
  );
};

const getEtatIcon = (etat) => {
  const t = (etat?.titre || '').toLowerCase();
  if (Number(etat?.id) === ETAT_HC_ID || t.includes('hors cible') || t.includes('hc ')) return FaThumbsDown;
  if (t.includes('brut')) return FaFlag;
  if (t.includes('debrif')) return FaHeadset;
  if (t.includes('ecoute')) return FaHeadphones;
  if (t.includes('verifier') || t.includes('vérifier')) return FaQuestionCircle;
  if (t.includes('manque') || t.includes('enregistrement')) return FaMicrophoneSlash;
  if (t.includes('doublon')) return FaClone;
  if (t.includes('nrp') || t.includes('injoignable')) return FaPhoneSlash;
  if (t.includes('poubelle') || t.includes('archive')) return FaTrash;
  if (t.includes('attente')) return FaHourglassHalf;
  if (t.includes('ko')) return FaTimes;
  if (t.includes('valid')) return FaCheckCircle;
  if (t.includes('rappel')) return FaBell;
  if (t.includes('negatif') || t.includes('négatif')) return FaMinusCircle;
  if (t.includes('alerte')) return FaExclamationTriangle;
  if (t.includes('a verifier') || t.includes('à verifier')) return FaSearch;
  return FaFlag;
};

const isSingleDayDateRange = (dateDebut, dateFin) =>
  Boolean(dateDebut && dateFin && dateDebut === dateFin);

const buildControleQualiteParams = (filters) => {
  const params = { ...filters };
  Object.keys(params).forEach((key) => {
    if (params[key] === '' || params[key] === null) delete params[key];
  });
  if (isSingleDayDateRange(params.date_debut, params.date_fin)) {
    params.page = 1;
    params.all = '1';
    delete params.limit;
  } else {
    delete params.all;
    params.limit = PAGE_SIZE_MULTI_DAY;
  }
  return params;
};

const ControleQualite = () => {
  useForceDesktopViewport('controle-qualite-page');
  const { user } = useAuth();
  const { openFicheDetail } = useFicheDetailModal();
  const { setAutoHide, closeSidebar, isDesktop } = useSidebar();
  const queryClient = useQueryClient();

  // Sidebar réduit par défaut à l'arrivée sur la page (l'utilisateur peut le rouvrir via le menu)
  useEffect(() => {
    if (isDesktop) {
      setAutoHide(true);
    } else {
      closeSidebar();
    }
    return () => setAutoHide(false);
    // setAutoHide / closeSidebar : refs stables ; ne pas ré-exécuter au toggle (évite de re-bloquer le menu)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  useEffect(() => {
    document.body.classList.add('controle-qualite-page');
    return () => document.body.classList.remove('controle-qualite-page');
  }, []);
  const [showFilters, setShowFilters] = useState(true);
  const [contextMenu, setContextMenu] = useState(null);
  const [quickSearch, setQuickSearch] = useState('');
  const [filters, setFilters] = useState({
    page: 1,
    id_agent: '',
    id_etat_final: '',
    date_debut: new Date().toISOString().split('T')[0], // Date du jour par défaut
    date_fin: new Date().toISOString().split('T')[0] // Date du jour par défaut
  });
  const brutDefaultAppliedRef = useRef(false);
  
  // État pour gérer l'édition du commentaire qualité
  const [editingComment, setEditingComment] = useState({ hash: null, value: '' });
  // Confirmateur à enregistrer dans l'historique (RE, RP Confirmation, admin, backoffice)
  const [histoConfirmateurId, setHistoConfirmateurId] = useState('');
  
  // État pour le modal KO
  const [koModal, setKoModal] = useState({
    isOpen: false,
    ficheHash: null,
    motifKo: '',
    commentaireComplement: '',
  });
  
  // État pour le modal HC
  const [hcModal, setHcModal] = useState({
    isOpen: false,
    ficheHash: null,
    sousEtatId: '',
    commentaire: ''
  });

  // État pour le modal Alerte KO (type PERSO/TECHNIQUE + commentaire)
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    ficheHash: null,
    fiche: null,
    typeAlerte: 'PERSO',
    commentaire: '',
    nbAlertes: null
  });

  // Modal commentaire qualité (avant validation si commentaire manquant)
  const [commentQualiteModal, setCommentQualiteModal] = useState({
    isOpen: false,
    ficheHash: null,
    commentaire: '',
  });

  // Modal Remarques (envoyer / consulter les remarques qualité → agents qualification)
  const [remarquesModalOpen, setRemarquesModalOpen] = useState(false);
  const [remarquesFicheContext, setRemarquesFicheContext] = useState(null);

  // Fiche sélectionnée (double-clic) pour activer le panneau d'états à droite
  const [selectedFicheHash, setSelectedFicheHash] = useState(null);
  const panelAnchorRef = useRef(null);
  const panelRef = useRef(null);

  // Récupérer les agents qualification
  const { data: agentsData } = useQuery('agents-qualif-list', async () => {
    const res = await api.get('/management/utilisateurs');
    return res.data.data?.filter(u => u.fonction === 3) || [];
  });

  // Récupérer les états du groupe 0 uniquement (sans "En-Attente") pour les filtres et la sélection
  const { data: etatsData } = useQuery('etats-groupe-0-sans-en-attente', async () => {
    const res = await api.get('/management/etats');
    // Filtrer uniquement les états du groupe 0, exclure "En-Attente" (ID 1 ou titre contenant "ATTENTE")
    return res.data.data?.filter(e => {
      const isGroupe0 = (e.groupe === '0' || e.groupe === 0);
      const isEnAttente = e.id === 1 || 
                         (e.titre && (e.titre.toUpperCase().includes('ATTENTE') || e.titre.toUpperCase() === 'EN-ATTENTE'));
      return isGroupe0 && !isEnAttente;
    }) || [];
  });

  // Par défaut : filtre État = BRUT (dès que la liste des états est dispo)
  useEffect(() => {
    if (brutDefaultAppliedRef.current || !etatsData?.length) return;
    const etatBrut = findEtatBrut(etatsData);
    brutDefaultAppliedRef.current = true;
    if (!etatBrut) return;
    setFilters((prev) => {
      if (prev.id_etat_final) return prev;
      return { ...prev, id_etat_final: String(etatBrut.id), page: 1 };
    });
  }, [etatsData]);

  // Récupérer tous les états pour vérifier si un état est groupe 0 ou non
  const { data: allEtatsData } = useQuery('all-etats', async () => {
    const res = await api.get('/management/etats');
    return res.data.data || [];
  });

  // Confirmateurs (pour RE/RP/admin/backoffice : choix du confirmateur à enregistrer dans l'historique)
  const { data: confirmateursData } = useQuery('confirmateurs-list', async () => {
    const res = await api.get('/management/utilisateurs');
    return res.data.data?.filter(u => u.fonction === 6 && (u.etat > 0 || u.etat == null)) || [];
  });
  const confirmateurs = confirmateursData || [];
  const showHistoConfirmateurDropdown = [1, 7, 13, 14].includes(Number(user?.fonction));

  // Récupérer les sous-états pour l'état HC (id 55)
  const { data: sousEtatsHcData } = useQuery('sous-etats-hc', async () => {
    const res = await api.get(`/management/sous-etat/${ETAT_HC_ID}`);
    return res.data.data || [];
  });

  // Nombre d'alertes KO pour la fiche du modal (quand le modal alerte est ouvert)
  const { data: alertesKoData } = useQuery(
    ['alertes-ko-count', alertModal.ficheHash],
    async () => {
      const res = await api.get(`/fiches/${alertModal.ficheHash}/alertes-ko`);
      return res.data;
    },
    { enabled: !!alertModal.ficheHash && alertModal.isOpen }
  );

  // Sous-états pour l'état sélectionné dans le modal alerte (états autres que KO/HC)
  // Récupérer les fiches avec rafraîchissement automatique toutes les 3 secondes
  const { data: fichesData, isLoading, error, refetch } = useQuery(
    ['controle-qualite', filters],
    async () => {
      try {
        const params = buildControleQualiteParams(filters);
        const res = await api.get('/fiches/controle-qualite', { params });
        // Vérifier la structure de la réponse
        if (res.data && res.data.success) {
          return res.data;
        } else {
          throw new Error(res.data?.message || 'Format de réponse inattendu');
        }
      } catch (error) {
        throw error;
      }
    },
    {
      // Rafraîchissement automatique toutes les 3 secondes
      refetchInterval: 3000,
      // Rafraîchir quand la fenêtre redevient active
      refetchOnWindowFocus: true,
      // Rafraîchir quand la connexion est rétablie
      refetchOnReconnect: true,
      onError: (error) => {
        console.error('Erreur useQuery contrôle qualité:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Erreur lors du chargement des fiches';
        toast.error(errorMessage);
      }
    }
  );

  // Mutation pour modifier rapidement l'état
  const updateEtatMutation = useMutation(
    async ({ hash, id_etat_final }) => {
      const res = await api.put(`/fiches/${hash}/etat-rapide`, { id_etat_final });
      return res.data;
    },
    {
      onSuccess: () => {
        // Invalider et rafraîchir immédiatement pour tous les utilisateurs
        queryClient.invalidateQueries(['controle-qualite']);
        refetch();
        toast.success('État mis à jour avec succès');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour de l\'état');
      }
    }
  );

  // Mutation pour valider une fiche (passer en En-Attente)
  const validateQualiteMutation = useMutation(
    async (hash) => {
      const res = await api.put(`/fiches/${hash}/valider-qualite`);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['controle-qualite']);
        refetch();
        toast.success('Fiche validée et passée en état "En-Attente"');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la validation');
      }
    }
  );

  // Mutation pour valider en KO : En-Attente + ko = 1 (fiche utilisée mais non comptabilisée pour l'agent)
  const validateQualiteKoMutation = useMutation(
    async ({ hash, motif_ko, commentaire_complement }) => {
      const res = await api.put(`/fiches/${hash}/valider-qualite-ko`, {
        motif_ko,
        commentaire_complement: commentaire_complement || null,
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['controle-qualite']);
        refetch();
        toast.success('Fiche validée (KO) : En-Attente, non comptabilisée pour l\'agent');
        // Fermer le modal
        setKoModal({ isOpen: false, ficheHash: null, motifKo: '', commentaireComplement: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la validation KO');
      }
    }
  );

  // Mutation pour valider en HC : état HC + sous-état sélectionné (fiche hors cible)
  const validateQualiteHcMutation = useMutation(
    async ({ hash, id_sous_etat, commentaire_hc }) => {
      const res = await api.put(`/fiches/${hash}/valider-qualite-hc`, {
        id_sous_etat,
        commentaire_hc
      });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['controle-qualite']);
        refetch();
        toast.success('Fiche validée (HC) : état HC, hors cible');
        // Fermer le modal
        setHcModal({ isOpen: false, ficheHash: null, sousEtatId: '', commentaire: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la validation HC');
      }
    }
  );

  // Mutation pour envoyer une alerte KO (type PERSO/TECHNIQUE + commentaire)
  const sendAlerteKoMutation = useMutation(
    async ({ hash, type_alerte, commentaire }) => {
      const res = await api.post(`/fiches/${hash}/alerte-ko`, {
        type_alerte: type_alerte || 'PERSO',
        commentaire: commentaire || null,
        from_controle_qualite: true
      });
      return res.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['controle-qualite']);
        queryClient.invalidateQueries(['alertes-ko-count']);
        refetch();
        toast.success(data?.message || `Alerte ${data?.data?.num_alerte}/3 envoyée à l'agent`);
        closeAlertModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi de l\'alerte');
      }
    }
  );

  // Mutation pour mettre à jour le commentaire qualité
  const updateCommentaireQualiteMutation = useMutation(
    async ({ hash, commentaire_qualite }) => {
      const res = await api.patch(`/fiches/${hash}/field`, {
        field: 'commentaire_qualite',
        value: commentaire_qualite,
        from_controle_qualite: true
      });
      return res.data;
    },
    {
      onSuccess: () => {
        // Invalider et rafraîchir immédiatement pour tous les utilisateurs
        queryClient.invalidateQueries(['controle-qualite']);
        refetch();
        toast.success('Commentaire qualité enregistré avec succès');
        setEditingComment({ hash: null, value: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de l\'enregistrement du commentaire');
      }
    }
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key !== 'page' ? { page: 1 } : {})
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleEtatChange = (hash, newEtatId) => {
    if (!newEtatId) return;
    updateEtatMutation.mutate({
      hash,
      id_etat_final: parseInt(newEtatId),
      histo_id_confirmateur: showHistoConfirmateurDropdown ? (histoConfirmateurId || undefined) : undefined
    });
  };

  // Quand on choisit HC dans la liste : ouvrir le modal HC (KO → modal motif)
  const handleNouvelEtatSelect = (fiche, newEtatId) => {
    const id = newEtatId ? parseInt(newEtatId, 10) : null;
    if (id === ETAT_KO_ID) {
      openKoModalForFiche(fiche);
      return;
    }
    if (id === ETAT_HC_ID) {
      openHcModal(fiche);
      return;
    }
    handleEtatChange(fiche.hash, newEtatId);
  };

  const getEtatColor = (etat) => {
    return etat?.color || '#cccccc';
  };

  const getQualiteAgentLabel = (fiche) => {
    if (fiche.qualite_assignee_pseudo) return fiche.qualite_assignee_pseudo;
    if (fiche.qualite_user_pseudo) return fiche.qualite_user_pseudo;
    return '';
  };

  const getEtatSubmenuItemStyle = (etat) => ({
    backgroundColor: getEtatColor(etat),
    color: '#ffffff',
    fontWeight: 600
  });

  // Vérifier si un état est groupe 0
  const isEtatGroupe0 = (id_etat_final) => {
    if (!id_etat_final || !allEtatsData) return false;
    const etat = allEtatsData.find(e => e.id === id_etat_final);
    return etat && (etat.groupe === '0' || etat.groupe === 0);
  };

  const isFicheEtatHc = (fiche) => Number(fiche.id_etat_final) === ETAT_HC_ID;

  // Obtenir le libellé à afficher pour l'état actuel
  // "Validée" / "Validée (KO)" uniquement si l'état n'est pas dans le groupe 0
  const getEtatActuelLabel = (fiche) => {
    if (fiche.archive === 1 || fiche.archive === '1') {
      return 'POUBELLE';
    }
    if (fiche.ko === 1 || fiche.ko === '1') {
      return 'VALIDÉ (KO)';
    }
    if (isFicheEtatHc(fiche)) {
      return fiche.etat_titre || 'HC';
    }
    if (isEtatGroupe0(fiche.id_etat_final)) {
      return fiche.etat_titre || '';
    }
    return 'Validé';
  };

  // Obtenir la couleur pour l'état actuel
  const getEtatActuelColor = (fiche) => {
    if (fiche.archive === 1 || fiche.archive === '1') {
      return '#6c757d';
    }
    if (fiche.ko === 1 || fiche.ko === '1') {
      return '#dc3545';
    }
    if (isFicheEtatHc(fiche)) {
      return getEtatColor(fiche);
    }
    if (isEtatGroupe0(fiche.id_etat_final)) {
      return getEtatColor(fiche);
    }
    return '#28a745';
  };

  const handleEditComment = (hash, currentComment) => {
    setEditingComment({ hash, value: currentComment || '' });
  };

  const handleSaveComment = (hash) => {
    const commentValue = editingComment.hash === hash ? editingComment.value : '';
    if (commentValue !== undefined) {
      updateCommentaireQualiteMutation.mutate({
        hash,
        commentaire_qualite: commentValue
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingComment({ hash: null, value: '' });
  };

  // Gestion des raccourcis clavier pour sauvegarder rapidement
  const handleKeyDown = (e, hash) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSaveComment(hash);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingComment({ hash: null, value: '' });
    }
  };

  const fichesRaw = fichesData?.data || [];

  const fiches = useMemo(() => {
    const term = quickSearch.trim().toLowerCase();
    if (!term) return fichesRaw;
    return fichesRaw.filter((fiche) => {
      const haystack = [
        fiche.nom,
        fiche.prenom,
        fiche.tel,
        fiche.cp,
        fiche.ville,
        fiche.agent_pseudo,
        fiche.qualite_assignee_pseudo,
        fiche.qualite_user_pseudo,
        fiche.etat_titre,
        fiche.commentaire,
        fiche.commentaire_qualite,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [fichesRaw, quickSearch]);

  const MSG_COMMENTAIRE_QUALITE_REQUIS =
    'Veuillez renseigner et enregistrer le commentaire qualité (colonne du tableau) avant de valider la fiche.';

  const hasCommentaireQualiteRenseigne = (fiche) =>
    String(fiche?.commentaire_qualite ?? '').trim().length > 0;

  const getFicheByHash = (hash) => fiches.find((f) => f.hash === hash);

  const requireCommentaireQualite = (fiche) => {
    if (!fiche || !hasCommentaireQualiteRenseigne(fiche)) {
      toast.warning(MSG_COMMENTAIRE_QUALITE_REQUIS);
      return false;
    }
    return true;
  };

  const handleValidateQualite = (fiche) => {
    if (!fiche) return;
    if (isFicheLockedForUser(fiche)) {
      toast.warning(getLockMessage(fiche));
      return;
    }
    if (!hasCommentaireQualiteRenseigne(fiche)) {
      setCommentQualiteModal({
        isOpen: true,
        ficheHash: fiche.hash,
        commentaire: fiche.commentaire_qualite || '',
      });
      return;
    }
    validateQualiteMutation.mutate(fiche.hash);
  };

  const closeCommentQualiteModal = () => {
    setCommentQualiteModal({ isOpen: false, ficheHash: null, commentaire: '' });
  };

  const handleCommentQualiteModalSubmit = () => {
    const commentaire = String(commentQualiteModal.commentaire ?? '').trim();
    if (!commentaire) {
      toast.warning('Veuillez saisir un commentaire qualité');
      return;
    }
    const ficheHash = commentQualiteModal.ficheHash;
    updateCommentaireQualiteMutation.mutate(
      { hash: ficheHash, commentaire_qualite: commentaire },
      {
        onSuccess: () => {
          closeCommentQualiteModal();
          validateQualiteMutation.mutate(ficheHash);
        },
      }
    );
  };

  const pagination = fichesData?.pagination || { page: 1, limit: PAGE_SIZE_MULTI_DAY, total: 0, pages: 1 };
  const singleDayRange = isSingleDayDateRange(filters.date_debut, filters.date_fin);
  const showPagination = !singleDayRange && pagination.pages > 1;
  const agents = agentsData || [];
  const etats = etatsData || [];
  const allEtats = allEtatsData || [];
  const sousEtatsHc = sousEtatsHcData || [];
  const { phase0: etatsPhase0, phase1: etatsPhase1, phase2: etatsPhase2, phase3: etatsPhase3 } = getEtatsGroupedByPhase(allEtats);
  const etatsPhase0SansKo = etatsPhase0.filter((e) => Number(e.id) !== ETAT_KO_ID);

  const syncEtatsPanelPosition = useCallback(() => {
    const anchor = panelAnchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    const margin = 16;
    const vh = window.innerHeight;
    const panelHeight = panel.offsetHeight;
    const top = Math.max(margin, vh - panelHeight - margin);

    panel.style.top = `${top}px`;
    panel.style.left = `${rect.left}px`;
    panel.style.width = `${rect.width}px`;
  }, []);

  useEffect(() => {
    syncEtatsPanelPosition();
    const onScrollOrResize = () => syncEtatsPanelPosition();

    window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true });
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('viewport-layout-change', onScrollOrResize);

    const scrollParents = [
      document.documentElement,
      document.body,
      document.querySelector('.content-wrapper'),
      document.querySelector('.controle-qualite .fiches-table-container'),
    ].filter(Boolean);

    scrollParents.forEach((el) => {
      el.addEventListener('scroll', onScrollOrResize, { passive: true });
    });

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(onScrollOrResize)
      : null;
    if (resizeObserver && panelRef.current) {
      resizeObserver.observe(panelRef.current);
    }

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, { capture: true });
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('viewport-layout-change', onScrollOrResize);
      scrollParents.forEach((el) => {
        el.removeEventListener('scroll', onScrollOrResize);
      });
      resizeObserver?.disconnect();
    };
  }, [syncEtatsPanelPosition, fiches.length, showFilters, etatsPhase0SansKo.length, selectedFicheHash]);

  // IDs des états pour lesquels tout agent qualité peut modifier (Debrief, À vérifier)
  const etatsQualiteOuverts = React.useMemo(() => {
    if (!allEtats || !allEtats.length) return [];
    return allEtats
      .filter(e => {
        const t = (e.titre || '').toLowerCase();
        return t.includes('debrif') || t.includes('verifier');
      })
      .map(e => e.id);
  }, [allEtats]);

  // Fiche verrouillée si validée (état hors groupe 0) pour tout le monde, ou si assignée à un autre agent qualité et état hors Debrief/À vérifier
  const isFicheLockedForUser = (fiche) => {
    // Fiche validée (Validé, En-Attente, KO, HC, etc.) : verrouillée pour tout le monde
    if (!isEtatGroupe0(fiche.id_etat_final)) return true;
    // Sinon : verrouillée si assignée à un autre agent qualité et état hors Debrief/À vérifier
    if (!user?.id || !fiche?.id_qualite) return false;
    if (Number(fiche.id_qualite) === Number(user.id)) return false;
    if (etatsQualiteOuverts.includes(Number(fiche.id_etat_final))) return false;
    return true;
  };

  const getLockMessage = (fiche) =>
    !isEtatGroupe0(fiche?.id_etat_final)
      ? 'Cette fiche est validée et verrouillée pour tout le monde.'
      : 'Cette fiche est déjà assignée à un autre agent qualité. Seul l\'agent assigné peut la modifier, sauf si l\'état est "Debrief" ou "À vérifier".';

  const selectedFiche = selectedFicheHash ? fiches.find((f) => f.hash === selectedFicheHash) : null;
  const etatsPanelActive = Boolean(selectedFiche && !isFicheLockedForUser(selectedFiche));

  useEffect(() => {
    if (selectedFicheHash && !fiches.some((f) => f.hash === selectedFicheHash)) {
      setSelectedFicheHash(null);
    }
  }, [fiches, selectedFicheHash]);

  // Fonctions pour gérer le modal KO
  const openKoModal = (fiche) => {
    setKoModal({
      isOpen: true,
      ficheHash: fiche.hash,
      motifKo: '',
      commentaireComplement: '',
    });
  };

  const openKoModalForFiche = (fiche) => {
    if (!fiche) return;
    if (isFicheLockedForUser(fiche)) {
      toast.warning(getLockMessage(fiche));
      return;
    }
    if (fiche.ko === 1 || fiche.ko === '1') {
      toast.info('Cette fiche est déjà en KO.');
      return;
    }
    openKoModal(fiche);
  };

  const closeKoModal = () => {
    setKoModal({ isOpen: false, ficheHash: null, motifKo: '', commentaireComplement: '' });
  };

  const handleKoModalSubmit = () => {
    if (!koModal.motifKo) {
      toast.warning('Veuillez sélectionner un motif KO');
      return;
    }
    if (
      String(koModal.motifKo).trim().toUpperCase() === 'AUTRE' &&
      !String(koModal.commentaireComplement || '').trim()
    ) {
      toast.warning('Veuillez préciser le motif (commentaire obligatoire pour AUTRE)');
      return;
    }
    const ficheKo = getFicheByHash(koModal.ficheHash);
    if (!requireCommentaireQualite(ficheKo)) return;
    validateQualiteKoMutation.mutate({
      hash: koModal.ficheHash,
      motif_ko: koModal.motifKo,
      commentaire_complement: koModal.commentaireComplement,
    });
  };

  const openKoFromMenu = () => {
    if (!contextMenu?.fiche) return;
    const fiche = contextMenu.fiche;
    closeContextMenu();
    openKoModalForFiche(fiche);
  };

  // Fonctions pour gérer le modal HC
  const openHcModal = (fiche) => {
    setHcModal({
      isOpen: true,
      ficheHash: fiche.hash,
      sousEtatId: fiche.id_sous_etat ? String(fiche.id_sous_etat) : '',
      commentaire: fiche.commentaire_qualite || ''
    });
  };

  const closeHcModal = () => {
    setHcModal({ isOpen: false, ficheHash: null, sousEtatId: '', commentaire: '' });
  };

  const handleHcModalSubmit = () => {
    if (!hcModal.sousEtatId) {
      toast.warning('Veuillez sélectionner un sous-état HC');
      return;
    }
    const selectedHcSousEtat = sousEtatsHc.find(
      (se) => String(se.id) === String(hcModal.sousEtatId)
    );
    const isHcAutre = String(selectedHcSousEtat?.titre || '').trim().toUpperCase() === 'AUTRE';
    const ficheHc = getFicheByHash(hcModal.ficheHash);
    const commentaireHcOk = String(hcModal.commentaire ?? '').trim().length > 0;
    if (isHcAutre && !commentaireHcOk) {
      toast.warning('Veuillez préciser le motif (commentaire obligatoire pour AUTRE)');
      return;
    }
    if (!hasCommentaireQualiteRenseigne(ficheHc) && !commentaireHcOk) {
      toast.warning(MSG_COMMENTAIRE_QUALITE_REQUIS);
      return;
    }
    validateQualiteHcMutation.mutate({
      hash: hcModal.ficheHash,
      id_sous_etat: parseInt(hcModal.sousEtatId),
      commentaire_hc: hcModal.commentaire
    });
  };

  // Modal Alerte KO : ouvrir / fermer / envoyer
  const openAlertModal = (fiche) => {
    setAlertModal({
      isOpen: true,
      ficheHash: fiche.hash,
      fiche,
      typeAlerte: 'PERSO',
      commentaire: '',
      nbAlertes: null
    });
  };

  const closeAlertModal = () => {
    setAlertModal({
      isOpen: false,
      ficheHash: null,
      fiche: null,
      typeAlerte: 'PERSO',
      commentaire: '',
      nbAlertes: null
    });
  };

  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = (ev) => {
      if (ev.target.closest?.('.cq-fiche-context-menu')) return;
      setContextMenu(null);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setContextMenu(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === 'Escape' && selectedFicheHash) {
        setSelectedFicheHash(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedFicheHash]);

  const handleRowDoubleClick = (e, fiche) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedFicheHash((prev) => (prev === fiche.hash ? null : fiche.hash));
  };

  const handleEtatPanelClick = (etat) => {
    if (!selectedFiche) return;
    if (isFicheLockedForUser(selectedFiche)) {
      toast.warning(getLockMessage(selectedFiche));
      return;
    }
    handleNouvelEtatSelect(selectedFiche, String(etat.id));
  };

  const openRowContextMenu = (e, fiche) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      fiche,
      x: e.clientX,
      y: e.clientY,
    });
  };

  const getContextMenuStyle = () => {
    if (!contextMenu || typeof window === 'undefined') {
      return { position: 'fixed', zIndex: 10050 };
    }
    const MENU_WIDTH = 240;
    const menuHeight = 320;
    const pad = 8;
    const spaceBelow = window.innerHeight - contextMenu.y;
    const openUpward = spaceBelow < menuHeight + pad && contextMenu.y > spaceBelow;
    const top = openUpward
      ? Math.max(pad, contextMenu.y - menuHeight)
      : Math.min(contextMenu.y, Math.max(pad, window.innerHeight - menuHeight - pad));
    const left = Math.min(
      Math.max(pad, contextMenu.x),
      Math.max(pad, window.innerWidth - MENU_WIDTH - pad)
    );
    return { position: 'fixed', top, left, zIndex: 10050 };
  };

  const closeContextMenu = () => setContextMenu(null);

  const openDetailsFromMenu = () => {
    if (contextMenu?.fiche?.hash) openFicheDetail(contextMenu.fiche.hash);
    closeContextMenu();
  };

  const handleEtatFromContextMenu = (etatId) => {
    if (!contextMenu?.fiche) return;
    const fiche = contextMenu.fiche;
    closeContextMenu();
    if (isFicheLockedForUser(fiche)) {
      toast.warning(getLockMessage(fiche));
      return;
    }
    handleNouvelEtatSelect(fiche, etatId);
  };

  const openAlertFromMenu = () => {
    if (!contextMenu?.fiche) return;
    const fiche = contextMenu.fiche;
    closeContextMenu();
    // Alerte autorisée même si fiche verrouillée / date d'insertion antérieure
    openAlertModal(fiche);
  };

  const openRemarqueFromMenu = () => {
    if (!contextMenu?.fiche) return;
    setRemarquesFicheContext(contextMenu.fiche);
    setRemarquesModalOpen(true);
    closeContextMenu();
  };

  const handleAlertModalSubmit = () => {
    const nbAlertes = alertesKoData?.nb_alertes ?? 0;
    const typeAlerte = alertModal.typeAlerte || 'PERSO';
    const nbCeType = typeAlerte === 'PERSO'
      ? (alertesKoData?.nb_alertes_perso_agent_mois ?? 0)
      : (alertesKoData?.nb_alertes_technique_agent_mois ?? 0);
    if (nbCeType >= 3) {
      toast.warning(`Cet agent a déjà reçu 3 alertes ${typeAlerte} ce mois-ci. Limite atteinte (3 par type).`);
      return;
    }
    if (nbAlertes >= 3) {
      toast.warning('3 alertes ont déjà été envoyées pour cette fiche.');
      return;
    }
    sendAlerteKoMutation.mutate({
      hash: alertModal.ficheHash,
      type_alerte: typeAlerte,
      commentaire: alertModal.commentaire || null
    });
  };

  return (
    <div className="controle-qualite">
      <SystemMessageBanner />
      <div className="page-header">
        <h1><FaUserCheck /> Contrôle Qualité</h1>
        <div className="page-header-actions">
          <button
            type="button"
            className="btn-remarques-header"
            onClick={() => { setRemarquesFicheContext(null); setRemarquesModalOpen(true); }}
            title="Ouvrir les remarques (qualité → agents qualification)"
          >
            <FaCommentDots />
          </button>
          <button 
            className="filter-toggle-btn" 
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> {showFilters ? 'Masquer' : 'Afficher'} les filtres
          </button>
        </div>
      </div>

      {/* Modal Remarques */}
      {remarquesModalOpen && (
        <div className="modal-overlay" onClick={() => { setRemarquesModalOpen(false); setRemarquesFicheContext(null); }}>
          <div className="modal-content remarques-modal" onClick={(e) => e.stopPropagation()}>
            <RemarquesContent
              onClose={() => { setRemarquesModalOpen(false); setRemarquesFicheContext(null); }}
              ficheContext={remarquesFicheContext}
            />
          </div>
        </div>
      )}

      {showFilters && (
        <div className="search-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Agent</label>
              <select
                value={filters.id_agent}
                onChange={(e) => handleFilterChange('id_agent', e.target.value)}
              >
                <option value="">Tous les agents</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.pseudo}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>État</label>
              <select
                value={filters.id_etat_final}
                onChange={(e) => handleFilterChange('id_etat_final', e.target.value)}
              >
                <option value="">Tous les états</option>
                {etatsPhase0SansKo.map(etat => (
                  <option key={etat.id} value={etat.id} style={{ backgroundColor: etat.color || '#cccccc' }}>
                    {etat.titre}
                  </option>
                ))}
                <option value="validated" style={{ backgroundColor: '#28a745' }}>
                  Validé
                </option>
                <option value="ko" style={{ backgroundColor: '#dc3545' }}>
                  KO
                </option>
              </select>
            </div>

            {showHistoConfirmateurDropdown && (
              <div className="form-group">
                <label>Confirmateur (historique)</label>
                <select
                  value={histoConfirmateurId}
                  onChange={(e) => setHistoConfirmateurId(e.target.value)}
                  title="Confirmateur enregistré dans l'historique lors du changement d'état"
                >
                  <option value="">Tout</option>
                  {confirmateurs.map(c => (
                    <option key={c.id} value={c.id}>{c.pseudo || `Utilisateur ${c.id}`}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Date début</label>
              <input
                type="date"
                value={filters.date_debut}
                onChange={(e) => handleFilterChange('date_debut', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Date fin</label>
              <input
                type="date"
                value={filters.date_fin}
                onChange={(e) => handleFilterChange('date_fin', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      <div className="results-info">
        <p>
          {quickSearch.trim()
            ? (
              <>
                Recherche rapide : <strong>{fiches.length}</strong> fiche{fiches.length > 1 ? 's' : ''}
                {' '}(sur {fichesRaw.length} chargée{fichesRaw.length > 1 ? 's' : ''})
              </>
            )
            : (
              <>
                Total: <strong>{pagination.total}</strong> fiches
              </>
            )}
          {showPagination && !quickSearch.trim() && (
            <> | Page <strong>{pagination.page}</strong> sur <strong>{pagination.pages}</strong></>
          )}
        </p>
      </div>

      <div className="quick-search-container">
        <FaSearch />
        <input
          type="text"
          className="quick-search-input"
          placeholder="Recherche rapide (nom, prénom, téléphone, CP, agent, état, commentaire agent, commentaire qualité)…"
          value={quickSearch}
          onChange={(e) => setQuickSearch(e.target.value)}
        />
        {quickSearch && (
          <button
            type="button"
            className="quick-search-clear"
            onClick={() => setQuickSearch('')}
            title="Effacer la recherche"
          >
            <FaTimes />
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="loading">Chargement des fiches...</div>
      ) : error ? (
        <div className="error">
          <p>Erreur lors du chargement des fiches</p>
          <p style={{ fontSize: '10.2px', color: '#666', marginTop: '10px' }}>
            {error.response?.data?.message || error.message || 'Erreur inconnue'}
          </p>
          {error.response?.data?.error && (
            <p style={{ fontSize: '9.4px', color: '#999', marginTop: '5px' }}>
              Détails: {error.response.data.error}
            </p>
          )}
          <button 
            onClick={() => refetch()} 
            style={{ marginTop: '15px', padding: '8px 16px', cursor: 'pointer' }}
          >
            Réessayer
          </button>
        </div>
      ) : fiches.length === 0 ? (
        <div className="no-results">Aucune fiche trouvée</div>
      ) : (
        <>
          <div className="cq-table-with-etats">
          <div className="fiches-table-container">
            <table className="fiches-table">
              <thead>
                <tr>
                  <FicheRowIndexHeader />
                  <th>Nom</th>
                  <th>Prénom</th>
                  <th>Téléphone</th>
                  <th>CP</th>
                  <th>Agent</th>
                  <th className="commentaire-agent-col">Commentaire agent</th>
                  <th>Agent qualité</th>
                  <th className="commentaire-qualite-col">Commentaire qualité</th>
                  <th className="etat-actuel-col">État actuel</th>
                  <th className="actions-col">Détails</th>
                </tr>
              </thead>
              <tbody>
                {fiches.map((fiche, rowIndex) => (
                  <tr
                    key={fiche.hash}
                    className={getFicheRowByEtatClassName(
                      getEtatActuelColor(fiche),
                      [
                        isFicheLockedForUser(fiche) ? 'fiche-row-locked' : '',
                        (fiche.archive === 1 || fiche.archive === '1') ? 'fiche-row-archive' : '',
                        contextMenu?.fiche?.hash === fiche.hash ? 'cq-row-context-active' : '',
                        selectedFicheHash === fiche.hash ? 'cq-row-selected' : '',
                      ].filter(Boolean).join(' ')
                    )}
                    style={{ backgroundColor: getEtatActuelColor(fiche) }}
                    onContextMenu={(e) => openRowContextMenu(e, fiche)}
                    onDoubleClick={(e) => handleRowDoubleClick(e, fiche)}
                    title="Double-clic : sélectionner pour changer l'état · Clic droit : menu d'actions"
                  >
                    <FicheRowIndexCell number={getFicheRowNumber(rowIndex)} />
                    <td>{fiche.nom || ''}</td>
                    <td>{fiche.prenom || ''}</td>
                    <td>{fiche.tel || ''}</td>
                    <td>{fiche.cp || ''}</td>
                    <td>{fiche.agent_pseudo || ''}</td>
                    <td className="commentaire-agent-col" title={fiche.commentaire || undefined}>
                      <div className="commentaire-agent-text">
                        {fiche.commentaire || ''}
                      </div>
                    </td>
                    <td className="agent-qualite-cell" title={getQualiteAgentLabel(fiche) ? `Agent qualité : ${getQualiteAgentLabel(fiche)}` : undefined}>
                      {getQualiteAgentLabel(fiche)}
                    </td>
                    <td className="commentaire-qualite-col">
                      <div className="comment-quick-edit-container">
                        <div className="comment-quick-actions">
                          {(() => {
                            const currentValue = editingComment.hash === fiche.hash ? editingComment.value : (fiche.commentaire_qualite || '');
                            const originalValue = fiche.commentaire_qualite || '';
                            const hasChanges = editingComment.hash === fiche.hash && currentValue !== originalValue;
                            
                            return hasChanges && !isFicheLockedForUser(fiche) && (
                              <>
                                <button
                                  className="btn-save-comment-quick"
                                  onClick={() => handleSaveComment(fiche.hash)}
                                  disabled={updateCommentaireQualiteMutation.isLoading}
                                  title="Enregistrer (Ctrl+Enter)"
                                >
                                  OK
                                </button>
                                <button
                                  className="btn-cancel-comment-quick"
                                  onClick={() => {
                                    setEditingComment({ hash: null, value: '' });
                                  }}
                                  disabled={updateCommentaireQualiteMutation.isLoading}
                                  title="Annuler (Echap)"
                                >
                                  <FaTimes />
                                </button>
                              </>
                            );
                          })()}
                        </div>
                        <textarea
                          value={editingComment.hash === fiche.hash ? editingComment.value : (fiche.commentaire_qualite || '')}
                          onChange={(e) => {
                            if (isFicheLockedForUser(fiche)) return;
                            if (editingComment.hash !== fiche.hash) {
                              setEditingComment({ hash: fiche.hash, value: e.target.value });
                            } else {
                              setEditingComment({ ...editingComment, value: e.target.value });
                            }
                          }}
                          onFocus={() => {
                            if (isFicheLockedForUser(fiche)) return;
                            if (editingComment.hash !== fiche.hash) {
                              setEditingComment({ hash: fiche.hash, value: fiche.commentaire_qualite || '' });
                            }
                          }}
                          onKeyDown={(e) => handleKeyDown(e, fiche.hash)}
                          className="comment-textarea-quick"
                          placeholder="Commentaire qualité… (Ctrl+Enter)"
                          rows={3}
                          readOnly={isFicheLockedForUser(fiche)}
                          title={isFicheLockedForUser(fiche) ? getLockMessage(fiche) : undefined}
                        />
                      </div>
                    </td>
                    <td className="etat-actuel-col etat-col-cell">
                      {isFicheLockedForUser(fiche) && (
                        <span className="fiche-locked-badge" title={getLockMessage(fiche)}>Verrouillée</span>
                      )}
                      <span className="etat-text etat-text--wrap" title={getEtatActuelLabel(fiche)}>
                        {getEtatActuelLabel(fiche)}
                      </span>
                    </td>
                    <td className="actions-col">
                      <div className="action-buttons action-buttons-minimal">
                        <FicheDetailLink
                          ficheHash={fiche.hash}
                          className="btn-detail-icon"
                          title="Détails fiche"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cq-etats-panel-sticky" ref={panelAnchorRef}>
          <aside
            ref={panelRef}
            className="cq-etats-panel cq-etats-panel--floating"
            aria-label="Changer l'état de la fiche sélectionnée"
          >
            <div className="cq-etats-panel-header">États</div>
            {selectedFiche ? (
              <div className="cq-etats-panel-fiche" title="Fiche sélectionnée (double-clic pour désélectionner)">
                {[selectedFiche.nom, selectedFiche.prenom].filter(Boolean).join(' ') || 'Fiche'}
                {selectedFiche.tel ? ` · ${selectedFiche.tel}` : ''}
              </div>
            ) : (
              <p className="cq-etats-panel-hint">Double-cliquez sur une fiche pour activer les boutons</p>
            )}
            <div className="cq-etats-panel-buttons">
              {etatsPhase0SansKo.map((etat) => {
                const EtatIcon = getEtatIcon(etat);
                const isCurrentEtat = Number(selectedFiche?.id_etat_final) === Number(etat.id);
                return (
                  <button
                    key={etat.id}
                    type="button"
                    className={[
                      'cq-etat-panel-btn',
                      isCurrentEtat ? 'cq-etat-panel-btn-current' : '',
                    ].filter(Boolean).join(' ')}
                    style={{
                      backgroundColor: getEtatColor(etat),
                      color: '#fff',
                    }}
                    disabled={!etatsPanelActive || updateEtatMutation.isLoading}
                    title={
                      !selectedFiche
                        ? 'Sélectionnez une fiche (double-clic)'
                        : isFicheLockedForUser(selectedFiche)
                          ? getLockMessage(selectedFiche)
                          : `Passer en ${etat.titre}`
                    }
                    onClick={() => handleEtatPanelClick(etat)}
                  >
                    <span className="cq-etat-panel-btn-icon" aria-hidden="true">
                      <EtatIcon />
                    </span>
                    <span className="cq-etat-panel-btn-label">{etat.titre}</span>
                  </button>
                );
              })}
              <button
                type="button"
                className="cq-etat-panel-btn cq-etat-panel-btn-valider"
                disabled={
                  !etatsPanelActive ||
                  validateQualiteMutation.isLoading ||
                  updateCommentaireQualiteMutation.isLoading
                }
                title={
                  !selectedFiche
                    ? 'Sélectionnez une fiche (double-clic)'
                    : isFicheLockedForUser(selectedFiche)
                      ? getLockMessage(selectedFiche)
                      : 'Valider et passer en En-Attente'
                }
                onClick={() => {
                  if (!selectedFiche) return;
                  handleValidateQualite(selectedFiche);
                }}
              >
                <span className="cq-etat-panel-btn-icon" aria-hidden="true">
                  <FaCheckCircle />
                </span>
                <span className="cq-etat-panel-btn-label">Valider</span>
              </button>
              <button
                type="button"
                className="cq-etat-panel-btn cq-etat-panel-btn-ko"
                disabled={
                  !etatsPanelActive ||
                  validateQualiteKoMutation.isLoading ||
                  selectedFiche?.ko === 1 ||
                  selectedFiche?.ko === '1'
                }
                title={
                  !selectedFiche
                    ? 'Sélectionnez une fiche (double-clic)'
                    : selectedFiche?.ko === 1 || selectedFiche?.ko === '1'
                      ? 'Déjà en KO'
                      : 'Mettre en KO'
                }
                onClick={() => openKoModalForFiche(selectedFiche)}
              >
                <span className="cq-etat-panel-btn-icon" aria-hidden="true">
                  <FaBan />
                </span>
                <span className="cq-etat-panel-btn-label">Mettre en KO</span>
              </button>
            </div>
          </aside>
          </div>
          </div>

          {showPagination && (
            <div className="pagination">
              <button
                type="button"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Précédent
              </button>
              <span>
                Page {pagination.page} sur {pagination.pages}
              </span>
              <button
                type="button"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}

      {contextMenu && (
        <div
          className="cq-fiche-context-menu"
          style={getContextMenuStyle()}
          role="menu"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cq-context-menu-row-label" title="Ligne sélectionnée">
            {[contextMenu.fiche.nom, contextMenu.fiche.prenom].filter(Boolean).join(' ') || 'Fiche'}
            {contextMenu.fiche.tel ? ` · ${contextMenu.fiche.tel}` : ''}
          </div>
          <button type="button" className="cq-fiche-context-menu-item" onClick={openDetailsFromMenu}>
            Détails
          </button>
          <div className="cq-context-menu-etat-group">
            <span className="cq-context-menu-etat-label">Changer état</span>
            <div className="cq-context-submenu">
              {etatsPhase0SansKo.map((etat) => (
                <button
                  key={etat.id}
                  type="button"
                  className="cq-fiche-context-menu-item cq-context-submenu-item"
                  style={getEtatSubmenuItemStyle(etat)}
                  disabled={updateEtatMutation.isLoading || isFicheLockedForUser(contextMenu.fiche)}
                  onClick={() => handleEtatFromContextMenu(String(etat.id))}
                >
                  {etat.titre}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="cq-fiche-context-menu-item"
            disabled={
              validateQualiteKoMutation.isLoading ||
              contextMenu.fiche.ko === 1 ||
              contextMenu.fiche.ko === '1' ||
              isFicheLockedForUser(contextMenu.fiche)
            }
            onClick={openKoFromMenu}
          >
            Mettre en KO
          </button>
          <button
            type="button"
            className="cq-fiche-context-menu-item"
            disabled={
              sendAlerteKoMutation.isLoading ||
              (contextMenu.fiche.nb_alertes ?? 0) >= 1
            }
            onClick={openAlertFromMenu}
          >
            Envoyer alerte
          </button>
          <button type="button" className="cq-fiche-context-menu-item" onClick={openRemarqueFromMenu}>
            Envoyer remarque
          </button>
        </div>
      )}

      <KoMotifModal
        isOpen={koModal.isOpen}
        title="Validation KO"
        submitLabel={validateQualiteKoMutation.isLoading ? 'Validation…' : 'Valider KO'}
        isLoading={validateQualiteKoMutation.isLoading}
        motifKo={koModal.motifKo}
        commentaireComplement={koModal.commentaireComplement}
        onMotifChange={(v) => setKoModal((m) => ({ ...m, motifKo: v }))}
        onCommentaireChange={(v) => setKoModal((m) => ({ ...m, commentaireComplement: v }))}
        onSubmit={handleKoModalSubmit}
        onClose={closeKoModal}
      />

      {/* Modal commentaire qualité (requis avant validation) */}
      {commentQualiteModal.isOpen && (
        <div className="modal-overlay" onClick={closeCommentQualiteModal}>
          <div className="modal-content commentaire-qualite-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaCommentDots /> Commentaire qualité</h3>
              <button type="button" className="modal-close-btn" onClick={closeCommentQualiteModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p className="commentaire-qualite-modal-hint">
                Un commentaire qualité est obligatoire pour valider la fiche et la passer en En-Attente.
              </p>
              <div className="form-group">
                <label>Commentaire qualité <span className="required">*</span></label>
                <textarea
                  value={commentQualiteModal.commentaire}
                  onChange={(e) => setCommentQualiteModal((m) => ({ ...m, commentaire: e.target.value }))}
                  className="commentaire-qualite-modal-textarea"
                  placeholder="Saisissez le commentaire qualité…"
                  rows={5}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={closeCommentQualiteModal}>
                Annuler
              </button>
              <button
                type="button"
                className="btn-confirm-valider"
                onClick={handleCommentQualiteModalSubmit}
                disabled={
                  updateCommentaireQualiteMutation.isLoading ||
                  validateQualiteMutation.isLoading ||
                  !String(commentQualiteModal.commentaire ?? '').trim()
                }
              >
                {updateCommentaireQualiteMutation.isLoading || validateQualiteMutation.isLoading
                  ? 'Enregistrement…'
                  : 'Enregistrer et valider'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal HC */}
      {hcModal.isOpen && (
        <div className="modal-overlay" onClick={closeHcModal}>
          <div className="modal-content hc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Validation HC (Hors Cible)</h3>
              <button className="modal-close-btn" onClick={closeHcModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Motif HC <span className="required">*</span></label>
                <select
                  value={hcModal.sousEtatId}
                  onChange={(e) => setHcModal({ ...hcModal, sousEtatId: e.target.value })}
                  className="hc-sous-etat-select"
                >
                  <option value="">-- Sélectionner un motif --</option>
                  {sousEtatsHc.map(se => (
                    <option key={se.id} value={se.id}>{se.titre}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  Commentaire
                  {String(
                    sousEtatsHc.find((se) => String(se.id) === String(hcModal.sousEtatId))?.titre || ''
                  )
                    .trim()
                    .toUpperCase() === 'AUTRE' ? (
                    <span className="required"> *</span>
                  ) : null}
                </label>
                <textarea
                  value={hcModal.commentaire}
                  onChange={(e) => setHcModal({ ...hcModal, commentaire: e.target.value })}
                  className="hc-commentaire-textarea"
                  placeholder={
                    String(
                      sousEtatsHc.find((se) => String(se.id) === String(hcModal.sousEtatId))?.titre || ''
                    )
                      .trim()
                      .toUpperCase() === 'AUTRE'
                      ? 'Précisez le motif…'
                      : 'Commentaire sur la raison du HC...'
                  }
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeHcModal}>
                Annuler
              </button>
              <button
                className="btn-confirm-hc"
                onClick={handleHcModalSubmit}
                disabled={
                  validateQualiteHcMutation.isLoading ||
                  !hcModal.sousEtatId ||
                  (String(
                    sousEtatsHc.find((se) => String(se.id) === String(hcModal.sousEtatId))?.titre || ''
                  )
                    .trim()
                    .toUpperCase() === 'AUTRE' &&
                    !String(hcModal.commentaire || '').trim())
                }
              >
                {validateQualiteHcMutation.isLoading ? 'Validation...' : 'Valider HC'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Alerte KO */}
      {alertModal.isOpen && alertModal.fiche && (
        <div className="modal-overlay" onClick={closeAlertModal}>
          <div className="modal-content alerte-ko-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FaBell /> Envoyer une alerte à l'agent</h3>
              <button type="button" className="modal-close-btn" onClick={closeAlertModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p className="alerte-ko-info">
                Alerte envoyée à l'agent qualification qui a inséré la fiche. Choisissez le type (PERSO ou TECHNIQUE) et un commentaire. Un agent peut recevoir jusqu'à 3 alertes PERSO et 3 alertes TECHNIQUE par mois. 3 alertes doivent être envoyées sur une fiche avant de pouvoir la passer en KO.
              </p>
              <div className="form-group">
                <label>Agent destinataire</label>
                <p className="modal-readonly-value">{alertModal.fiche.agent_pseudo || '-'}</p>
              </div>
              <div className="form-group">
                <label>Client</label>
                <p className="modal-readonly-value">
                  {alertModal.fiche.nom || '-'} {alertModal.fiche.prenom || ''} – {alertModal.fiche.tel || '-'}
                </p>
              </div>
              {alertesKoData != null && (
                <>
                  <p className="alerte-ko-count">
                    <strong>{alertesKoData.nb_alertes ?? 0}/3</strong> alertes pour cette fiche (avant passage KO)
                  </p>
                  <p className="alerte-ko-count">
                    <strong>{alertesKoData.nb_alertes_perso_agent_mois ?? 0}/3</strong> PERSO et <strong>{alertesKoData.nb_alertes_technique_agent_mois ?? 0}/3</strong> TECHNIQUE reçues par cet agent ce mois-ci (3 de chaque type autorisées)
                  </p>
                </>
              )}
              <div className="form-group">
                <label>Type d'alerte <span className="required">*</span></label>
                <select
                  value={alertModal.typeAlerte}
                  onChange={(e) => setAlertModal({ ...alertModal, typeAlerte: e.target.value })}
                  className="etat-select"
                >
                  <option value="PERSO">PERSO</option>
                  <option value="TECHNIQUE">TECHNIQUE</option>
                </select>
              </div>
              <div className="form-group">
                <label>Commentaire</label>
                <textarea
                  value={alertModal.commentaire}
                  onChange={(e) => setAlertModal({ ...alertModal, commentaire: e.target.value })}
                  className="commentaire-textarea"
                  placeholder="Message ou commentaire pour l'agent..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={closeAlertModal}>
                Annuler
              </button>
              <button
                type="button"
                className="btn-confirm-alerte"
                onClick={handleAlertModalSubmit}
                disabled={sendAlerteKoMutation.isLoading || (alertModal.typeAlerte === 'PERSO' ? (alertesKoData?.nb_alertes_perso_agent_mois ?? 0) : (alertesKoData?.nb_alertes_technique_agent_mois ?? 0)) >= 3 || (alertesKoData?.nb_alertes ?? 0) >= 3}
              >
                {sendAlerteKoMutation.isLoading ? 'Envoi...' : 'Envoyer l\'alerte'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControleQualite;

